import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ChevronRight, Shield, Award, Users, Star } from 'lucide-react';
import { getWhatsAppLink } from '../../lib/utils';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Find Doctors', href: '/doctors' },
    { name: 'Skin Treatments', href: '/category/skin' },
    { name: 'Hair Solutions', href: '/category/hair' },
    { name: 'Weight Management', href: '/category/weight' },
    { name: 'Aesthetic Procedures', href: '/category/aesthetic' },
    { name: 'Expert Blogs', href: '/blogs' },
  ];

  const seoLinks = [
    { name: 'Mumbai', href: '/landing/dermatologist-in-mumbai' },
    { name: 'Delhi', href: '/landing/dermatologist-in-delhi' },
    { name: 'Pune', href: '/landing/dermatologist-in-pune' },
    { name: 'Bangalore', href: '/landing/dermatologist-in-bangalore' },
    { name: 'Kolkata', href: '/landing/dermatologist-in-kolkata' },
    { name: 'Chennai', href: '/landing/dermatologist-in-chennai' },
    { name: 'Hyderabad', href: '/landing/dermatologist-in-hyderabad' },
    { name: 'Dubai', href: '/landing/dermatologist-in-dubai' },
    { name: 'London', href: '/landing/dermatologist-in-london' },
    { name: 'New York', href: '/landing/dermatologist-in-new-york' },
    { name: 'Paris', href: '/landing/dermatologist-in-paris' },
    { name: 'Tokyo', href: '/landing/dermatologist-in-tokyo' },
    { name: 'Sydney', href: '/landing/dermatologist-in-sydney' },
    { name: 'Zurich', href: '/landing/dermatologist-in-zurich' },
    { name: 'Boston', href: '/landing/dermatologist-in-boston' },
    { name: 'UK', href: '/landing/dermatologist-in-uk' },
    { name: 'USA', href: '/landing/dermatologist-in-usa' },
    { name: 'India', href: '/landing/dermatologist-in-india' },
  ];

  const companyLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'For Doctors', href: '/register' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
  ];

  const trustSignals = [
    { icon: Shield, label: 'Verified Doctors' },
    { icon: Award, label: 'Quality Assured' },
    { icon: Users, label: '50k+ Patients' },
    { icon: Star, label: 'Top Rated' },
  ];

  return (
    <footer className="bg-[#23265d] text-white">
      {/* SEO Links Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <h3 className="font-accent text-xl font-semibold text-white mb-6 text-center">
            Find Dermatologists Near You
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {seoLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full text-sm text-white/80 hover:text-white transition-all hover:scale-105"
                data-testid={`seo-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4" data-testid="footer-logo-link">
              <img src="/brand/logo-white.png" alt="DrPortals - Lead to Wellness" className="h-12 w-auto" data-testid="footer-logo-img" />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              India's most trusted platform for finding verified dermatologists. 
              Quality care, curated experts, exceptional results.
            </p>
            
            {/* Trust Signals */}
            <div className="grid grid-cols-2 gap-3">
              {trustSignals.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="flex items-center gap-2 text-xs text-white/60">
                    <Icon className="w-4 h-4 text-[#c5a065]" />
                    {item.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-1 group"
                  >
                    <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-white mb-5">Company</h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-1 group"
                  >
                    <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-5">Get In Touch</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={getWhatsAppLink("Hi, I need help finding a dermatologist.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-white/60 hover:text-white text-sm transition-colors"
                >
                  <Phone className="w-4 h-4 mt-0.5 text-[#25D366]" />
                  <span>WhatsApp Support<br />Available 24/7</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@drportals.com"
                  className="flex items-start gap-3 text-white/60 hover:text-white text-sm transition-colors"
                >
                  <Mail className="w-4 h-4 mt-0.5 text-[#c5a065]" />
                  <span>info@drportals.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/60 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#c5a065]" />
                  <span>Mumbai, India | Dubai, UAE</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Partner & Copyright */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm text-[#c5a065] font-medium">
                Founding Industry Partner: BTL Aesthetic India
              </p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-xs text-white/40">
                &copy; {currentYear} DrPortals. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
